/**
 * LandingV6 — unified search surface (VOY-477).
 *
 * Owns the route state machine on `/`:
 *   - State A (idle, `searchActive === false`): full Hero + marketing
 *     sections (SecondBrain, ZeroSection, Steps, Companion,
 *     Destinations, Pricing, Rewards, FAQ, FooterCTA).
 *   - State B (active, `searchActive === true`): compact Hero (sticky)
 *     + SearchResults, marketing sections unmounted.
 *
 * State management:
 *   - One `useSearchState` instance owned by this component, hydrated
 *     from URL on mount via `parseURL`.
 *   - One `useSearchPipeline` instance owned by this component, fed by
 *     `useSearchState` and consumed by SearchResults.
 *   - SmartSearchCard receives the same `useSearchState` instance via a
 *     `search` prop (VOY-477 plumbing) so the composer and the route
 *     share one state — no double machinery, no out-of-sync hydration.
 *
 * URL contract:
 *   - State A → `/`
 *   - State B → `/?<toURLParams()>` via `history.pushState` on submit,
 *     `history.replaceState` on hydration to avoid extra history entries.
 *   - Back/forward navigation re-runs `parseURL` so the state machine
 *     flips correctly without remounting the entire tree.
 *
 * Accessibility:
 *   - Focus moves to the SearchResults heading on State A → State B.
 *   - `<div aria-live="polite">` announces the results count when it
 *     stabilises ("5 flights and 247 hotels found").
 *
 * Transition:
 *   - 300ms opacity fade on the marketing-section container during
 *     A↔B. No height/transform animation — keeps layout calm and
 *     reduces motion-sickness risk.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "next-themes";
import { useFirstName } from "@/hooks/useFirstName";
import { useAuth } from "@/lib/auth";
import { useMounted } from "./useMounted";
// VOY-506: TopNav is now provided by the global AppShell in src/App.tsx.
// This page no longer renders its own <TopNav /> — the shared instance
// renders at the top of every route. Inline `initial` logic also moved
// to AppShell (computed once from auth user, threaded into TopNav).
import { Hero } from "./Hero";
import { SecondBrain } from "./SecondBrain";
import { ZeroSection } from "./ZeroSection";
import { Steps } from "./Steps";
import { Companion } from "./Companion";
import { Destinations } from "./Destinations";
import { Pricing } from "./Pricing";
import { Rewards } from "./Rewards";
import { FAQ } from "./FAQ";
import { FooterCTA } from "./FooterCTA";
import { Footer } from "./Footer";
import type { SearchState } from "@/lib/search/types";
import { useSearchState, toURLParams } from "@/lib/search/useSearchState";
import { useSearchActive } from "@/lib/search/useSearchActive";
import { useSearchPipeline, type RunSearchParams } from "@/lib/search/useSearchPipeline";
import { SearchResults } from "@/components/search/SearchResults";
import type { HotelFilters as PipelineHotelFilters } from "@/lib/search/pipeline/helpers";
import type { FlightSearchParamsForAnalysis } from "@/types/travel";
import { getUserLocationWithAirport } from "@/lib/geolocation";
import { toast } from "sonner";

import "@/styles/voy-v6.css";

/**
 * Geolocation fallback for missing flight origin. Mirrors the
 * pre-VOY-477 behaviour in `search-plus.tsx` (lines 1257-1294): when
 * the user submitted just a destination (e.g. "Miami"), the page tried
 * to resolve the user's nearest airport via geolocation before firing
 * the flight search. The pipeline hook is intentionally pure and
 * doesn't do this; the caller does. (PR #280 review fix.)
 *
 * Resolves to an `{ origin, originName }` tuple. If geolocation fails
 * or no nearby airport, returns `undefined` and the caller skips
 * flights (Flights tab hides, Hotels + Itinerary still run).
 */
async function resolveOriginViaGeolocation(): Promise<
  { origin: string; originName?: string } | undefined
> {
  try {
    const result = await getUserLocationWithAirport();
    if (result.airportCode) {
      return {
        origin: result.airportCode,
        originName: result.airportName ?? result.city,
      };
    }
  } catch (err) {
    console.warn("[LandingV6] geolocation fallback failed:", err);
  }
  return undefined;
}

/**
 * Translate the SearchState into the params shape `useSearchPipeline`
 * accepts (`toBookingPayload` is close but produces a loosely-typed
 * record; this adapter is strictly typed against `RunSearchParams`).
 */
function stateToPipelineParams(state: SearchState): RunSearchParams {
  return {
    tripType: state.tripType,
    origin: state.from?.code,
    originName: state.from?.label,
    destination: state.to?.code,
    destinationName: state.to?.label,
    departureDate: state.departureDate,
    returnDate: state.returnDate,
    travelers: {
      adults: state.passengers.adults,
      children: state.passengers.children,
      childrenAges: state.passengers.childrenAges,
    },
    cabinClass: state.cabin,
    ...(state.legs && state.legs.length > 0
      ? {
          segments: state.legs
            .filter((l) => l.from?.code && l.to?.code && l.date)
            .map((l) => ({
              origin: l.from!.code,
              originName: l.from!.label,
              destination: l.to!.code,
              destinationName: l.to!.label,
              departureDate: l.date!,
            })),
        }
      : {}),
    ...(state.imageSearch
      ? { imageSearch: true, imageMetadata: { detectedLandmarks: [state.imageSearch.description ?? ''] } }
      : {}),
  };
}

export function LandingV6() {
  const { resolvedTheme } = useTheme();
  const mounted = useMounted();
  const isDark = mounted ? resolvedTheme === "dark" : false;

  // Single search state owned by the route. SmartSearchCard receives
  // this via the `search` prop so the composer and the route share
  // one source of truth (no double state machine).
  const search = useSearchState();
  const pipeline = useSearchPipeline();
  // VOY-466: default Match-My-Taste ON for authed users on mount.
  // The pipeline's internal default is `false` (it shouldn't depend
  // on auth context); LandingV6 owns the "enable on auth" policy.
  // Anon users see the toggle's "Sign in to personalise" affordance
  // — see MatchMyTasteToggle.
  const { user } = useAuth();
  const matchToggleSeededRef = useRef(false);
  // Destructure the setter — its useCallback identity is stable, so
  // the effect runs only on actual auth flips. Depending on the full
  // `pipeline` object would re-fire on every pipeline state change
  // (new hotels loaded, loading flags, etc.) — the ref guard would
  // catch the duplicate but the wasted re-renders are avoidable.
  const { setPersonalizeHotels } = pipeline;
  useEffect(() => {
    if (matchToggleSeededRef.current) return;
    if (user) {
      matchToggleSeededRef.current = true;
      setPersonalizeHotels(true);
    }
  }, [user, setPersonalizeHotels]);
  // PR #280 review fix: searchActive must NOT derive from useSearchState's
  // transient `query` field — every keystroke in the composer mutates
  // `query` and would have remounted State B on the first character.
  // The hook now derives from the URL (via popstate + a programmatic
  // setter) so only submit/hydration flips the flag.
  const { searchActive, setSearchActive } = useSearchActive();

  const firstName = useFirstName();

  // -----------------------------------------------------------------
  // URL hydration (mount + back/forward navigation)
  // -----------------------------------------------------------------
  // On first mount, replay the URL search string into search state.
  // Then attach a popstate listener so back/forward also re-hydrates.
  const hasHydratedRef = useRef(false);
  useEffect(() => {
    if (hasHydratedRef.current) return;
    hasHydratedRef.current = true;
    if (typeof window === 'undefined') return;
    if (window.location.search) {
      search.parseURL(window.location.search);
    } else {
      // No URL params — try sessionStorage for handoffs from
      // RecentSearchesRow / home.tsx.
      search.parseSessionStorage();
    }
  }, [search]);

  useEffect(() => {
    const handlePop = () => {
      // Re-hydrate from whatever URL the browser navigated to. Empty
      // search string drops state back to INITIAL, which flips
      // searchActive to false and re-renders State A.
      search.parseURL(window.location.search);
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [search]);

  // -----------------------------------------------------------------
  // autoSearch: fire the pipeline immediately when the URL says so,
  // AND mark searchActive so State B mounts. The URL is the source of
  // truth for the flag; useSearchState carries the autoSearch bit.
  // -----------------------------------------------------------------
  // Tracks the autoSearch we've already consumed so re-renders don't
  // re-fire it. The hydration effect above sets state.autoSearch=true
  // when the URL carries the flag.
  const lastAutoSearchedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!search.state.autoSearch) return;
    // Fingerprint by the URL params so identical autoSearches don't
    // double-fire but a new autoSearch (user edits + resubmits) does.
    const fingerprint = search.toURLParams().toString();
    if (lastAutoSearchedRef.current === fingerprint) return;
    lastAutoSearchedRef.current = fingerprint;
    setSearchActive(true);
    pipeline.runSearch(stateToPipelineParams(search.state), {
      searchFor: {
        flights: search.state.chips.flights,
        hotels: search.state.chips.hotels,
        itinerary: search.state.chips.itinerary,
      },
      searchMethod: search.state.mode === 'classic' ? 'classic' : 'ai',
      rawInput: search.state.query,
    });
  }, [search, pipeline, setSearchActive]);

  // -----------------------------------------------------------------
  // handleSubmit — fired by SmartSearchCard. Update URL + pipeline.
  // -----------------------------------------------------------------
  const handleSubmit = useCallback(
    async (submitted: SearchState) => {
      // SmartSearchCard already dispatched setMany() for the parsed
      // state, but the dispatched update is async. Merge the submitted
      // state into the params directly so the pipeline sees the latest.
      const params = stateToPipelineParams(submitted);
      // PR #280 review fix: when flights are requested but origin is
      // missing (typical "Tokyo" / "Miami" single-token submits), try
      // to resolve the user's nearest airport via geolocation BEFORE
      // calling pipeline.runSearch. Matches pre-VOY-477 search-plus
      // behaviour. The pipeline itself stays pure; the caller does the
      // ambient-context work.
      if (submitted.chips.flights && !params.origin && params.destination) {
        const resolved = await resolveOriginViaGeolocation();
        if (resolved) {
          params.origin = resolved.origin;
          params.originName = resolved.originName;
        } else {
          // Surface why flights might be empty so the user can either
          // grant location permission or set FROM manually via the
          // EditablePillRow above the results. Without this, the
          // Flights tab is just missing/empty with no explanation.
          toast.info(
            'Set a from-airport to search flights, or allow location access.',
            { duration: 6000 },
          );
        }
      }

      // Concurrent-submit caveat (PR #280 review item): a rapid
      // double-submit could let the first runSearch resolve after the
      // second, overwriting fresh results with stale. Low-risk here
      // because the toast-or-geolocate path serialises typical user
      // intent, but the pipeline already accepts a `signal` in its
      // options — TODO(VOY-477-followup): wire an AbortController per
      // submit so the in-flight fetch is cancelled cleanly.

      // PR #280 review fix: build the URL via `toURLParams` so the
      // canonical encoder is the single source of truth (the manual
      // re-encoding here previously dropped `autoSearch` and would
      // have silently dropped any new field added to toURLParams,
      // e.g. multi-city `segments`). We compose a SearchState that
      // overlays:
      //   - the geolocation-resolved origin (if any) onto `from`
      //   - `autoSearch: true` so page refresh / shared URLs re-fire
      //     the pipeline via the autoSearch effect in this component
      const fromForUrl =
        params.origin
          ? { code: params.origin, label: params.originName }
          : submitted.from;
      const stateForUrl: SearchState = {
        ...submitted,
        from: fromForUrl,
        autoSearch: true,
      };
      const urlParams = toURLParams(stateForUrl);

      // History entry: pushState so back-button returns to State A
      // cleanly. Each fresh search submit creates a new entry.
      const nextUrl = urlParams.toString() ? `/?${urlParams.toString()}` : '/';
      window.history.pushState({}, '', nextUrl);
      // pushState doesn't fire popstate; sync the flag manually.
      setSearchActive(true);

      pipeline.runSearch(params, {
        searchFor: {
          flights: submitted.chips.flights,
          hotels: submitted.chips.hotels,
          itinerary: submitted.chips.itinerary,
        },
        searchMethod: submitted.mode === 'classic' ? 'classic' : 'ai',
        rawInput: submitted.query,
      });
    },
    [pipeline, setSearchActive],
  );

  // Clear-search affordance lives on the TopNav brand-link in this PR:
  // clicking "Voy" already navigates to `/`, which triggers a fresh
  // mount with empty URL params → searchActive flips to false. No
  // dedicated "clear" button is in the design today; a back-button or
  // a brand-click is the canonical escape hatch.

  // -----------------------------------------------------------------
  // a11y: move focus to the results heading when State B mounts.
  // -----------------------------------------------------------------
  const resultsRegionRef = useRef<HTMLDivElement | null>(null);
  const prevActiveRef = useRef<boolean>(false);
  useEffect(() => {
    if (searchActive && !prevActiveRef.current && resultsRegionRef.current) {
      // Wait a tick for the panel to mount, then focus it. The region
      // has tabIndex={-1} so it's programmatically focusable without
      // entering the tab order.
      const id = requestAnimationFrame(() => {
        resultsRegionRef.current?.focus();
      });
      prevActiveRef.current = true;
      return () => cancelAnimationFrame(id);
    }
    if (!searchActive) prevActiveRef.current = false;
  }, [searchActive]);

  // -----------------------------------------------------------------
  // a11y: announce the results count when it stabilises.
  // -----------------------------------------------------------------
  const announcement = useMemo(() => {
    if (!searchActive) return '';
    if (pipeline.isLoadingFlights || pipeline.isLoadingHotels || pipeline.isLoadingItinerary) {
      return 'Searching…';
    }
    const parts: string[] = [];
    if (pipeline.searchedForFlights) {
      parts.push(`${pipeline.flights.length} flight${pipeline.flights.length === 1 ? '' : 's'}`);
    }
    if (pipeline.searchedForHotels) {
      parts.push(`${pipeline.hotelTotal} hotel${pipeline.hotelTotal === 1 ? '' : 's'}`);
    }
    if (pipeline.searchedForItinerary && pipeline.itinerary) {
      parts.push('itinerary ready');
    }
    return parts.length > 0 ? `${parts.join(', ')} found.` : '';
  }, [
    searchActive,
    pipeline.isLoadingFlights,
    pipeline.isLoadingHotels,
    pipeline.isLoadingItinerary,
    pipeline.flights.length,
    pipeline.hotelTotal,
    pipeline.itinerary,
    pipeline.searchedForFlights,
    pipeline.searchedForHotels,
    pipeline.searchedForItinerary,
  ]);

  // -----------------------------------------------------------------
  // Hotel filters: SearchResults owns the filter state but the
  // pipeline's `fetchHotelsPage` needs to know the current filters for
  // cache-key consistency. Lift filter state up here so it survives a
  // mode swap and can be passed both into SearchResults and the
  // pipeline's pagination handler.
  // -----------------------------------------------------------------
  const [hotelFilters, setHotelFilters] = useState<PipelineHotelFilters>({});
  const [hotelViewMode, setHotelViewMode] = useState<'list' | 'map' | 'both'>('list');

  // VOY-489 follow-up: hotel filter changes trigger a re-fetch. Now
  // that FilterRowHotels uses a stage-and-Apply pattern, each
  // dimension produces ONE filter change per Apply click — so a small
  // 200ms debounce here is enough to coalesce any near-simultaneous
  // emits (e.g. toggle pills + sort fired in quick succession). The
  // prior 700ms was a workaround for emit-on-every-tick that's no
  // longer needed; trimmed to 200ms for snappier feedback.
  //
  // We key off `pipeline.searchedForHotels` so we don't re-fetch
  // before the first search has even happened — without this guard,
  // mounting LandingV6 would fire a fetch against an empty
  // hotelSearchParams destination and silently no-op.
  const initialHotelFilterMount = useRef(true);
  useEffect(() => {
    if (initialHotelFilterMount.current) {
      initialHotelFilterMount.current = false;
      return;
    }
    if (!pipeline.searchedForHotels) return;
    const timer = setTimeout(() => {
      pipeline.fetchHotelsPage(1, hotelFilters).catch(() => {});
    }, 200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(hotelFilters)]);

  // PR #280 review fix: derive FlightSearchParamsForAnalysis so
  // CollapsibleItineraryPreview can fire the Flight Analysis fetch.
  // Without this the inner StructuredItineraryView renders only the
  // Timeline + Essentials tabs; "Analysis" requires `searchParams`
  // AND `availableFlights.length > 0` to populate. The shape matches
  // the one pre-VOY-477 search-plus.tsx constructed (lines 1638-1668).
  const flightSearchParamsForAnalysis = useMemo<FlightSearchParamsForAnalysis | undefined>(() => {
    const origin = search.state.from?.code;
    const destination = search.state.to?.code;
    const depDate = search.state.departureDate;
    if (!origin || !destination || !depDate) return undefined;
    return {
      origin,
      destination,
      departureDate: depDate.split('T')[0],
      returnDate: search.state.returnDate?.split('T')[0],
      passengers: {
        adults: search.state.passengers.adults,
        children: search.state.passengers.children,
      },
      tripType: search.state.tripType,
    };
  }, [
    search.state.from?.code,
    search.state.to?.code,
    search.state.departureDate,
    search.state.returnDate,
    search.state.passengers.adults,
    search.state.passengers.children,
    search.state.tripType,
  ]);

  // VOY-506: `initial` formerly computed here for the inline <TopNav />;
  // now provided by AppShell. Removed to drop the unused local.

  return (
    <div
      className={`voy-v6${isDark ? " dark" : ""}${searchActive ? ' searching' : ''}`}
      data-search-active={searchActive ? 'true' : 'false'}
    >
      <div className="page">
        {/* VOY-506: TopNav lives at the global AppShell level now. */}
        <Hero
          mode={searchActive ? 'compact' : 'full'}
          userFirstName={firstName}
          onSubmit={handleSubmit}
          search={search}
        />

        {/* aria-live region: announces results count to screen readers
            without focusing it. Visually hidden via sr-only utility. */}
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {announcement}
        </div>

        <div className="lv6-stage" data-state={searchActive ? 'b' : 'a'}>
          {searchActive ? (
            <div
              ref={resultsRegionRef}
              tabIndex={-1}
              role="region"
              aria-label="Search results"
              className="lv6-results"
              key="state-b"
            >
              <SearchResults
                flights={pipeline.flights}
                hotels={pipeline.hotels}
                totalHotelsBeforeFilters={pipeline.hotelTotal}
                hasActiveFilters={Object.keys(hotelFilters).length > 0}
                onClearFilters={() => setHotelFilters({})}
                isLoading={pipeline.isSearching}
                isLoadingFlights={pipeline.isLoadingFlights}
                isLoadingHotels={pipeline.isLoadingHotels}
                isLoadingItinerary={pipeline.isLoadingItinerary}
                hotelRetryState={pipeline.hotelRetryState}
                itinerary={pipeline.itinerary}
                isGeneratingItinerary={pipeline.isGeneratingItinerary}
                tripType={search.state.tripType}
                hotelCheckin={pipeline.hotelSearchParams.checkin}
                hotelCheckout={pipeline.hotelSearchParams.checkout}
                hotelGuests={pipeline.hotelSearchParams.guests}
                hotelTotal={pipeline.hotelTotal}
                hotelPage={pipeline.hotelPage}
                hotelPerPage={pipeline.hotelPerPage}
                hotelTotalPages={pipeline.hotelTotalPages}
                onHotelPageChange={(p) => {
                  pipeline.fetchHotelsPage(p, hotelFilters).catch(() => {});
                }}
                searchedForFlights={pipeline.searchedForFlights}
                searchedForHotels={pipeline.searchedForHotels}
                searchedForItinerary={pipeline.searchedForItinerary}
                suggestions={pipeline.hotelSuggestions}
                widened={pipeline.widenedHotels}
                hotelFilters={hotelFilters}
                onHotelFiltersChange={(f) => setHotelFilters(f as PipelineHotelFilters)}
                hotelViewMode={hotelViewMode}
                onHotelViewModeChange={setHotelViewMode}
                searchParams={flightSearchParamsForAnalysis}
                personalizeHotels={pipeline.personalizeHotels}
                onPersonalizeHotelsChange={pipeline.setPersonalizeHotels}
                personalizeFlights={pipeline.personalizeFlights}
                onPersonalizeFlightsChange={pipeline.setPersonalizeFlights}
              />
            </div>
          ) : (
            <div className="lv6-marketing" key="state-a">
              <SecondBrain />
              <ZeroSection />
              <Steps />
              <Companion />
              <Destinations />
              <Pricing />
              <Rewards />
              <FAQ />
              <FooterCTA />
            </div>
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
}
